import { Stethoscope, ListChecks, SearchCheck, Camera, Loader2, RotateCcw } from "lucide-react";
import { fmtDate } from "@/lib/api";
import ResultDetails from "@/components/analysis/ResultDetails";
import ResultConsult from "@/components/analysis/ResultConsult";
import ResultConsultV2 from "@/components/analysis/ResultConsultV2";
import { Section } from "@/components/analysis/resultUi";

const STATUS_STYLE = {
  probabile: "bg-[#15803D]/10 text-[#15803D] border-[#15803D]/40",
  da_confermare: "bg-[#D97706]/10 text-[#9A4A05] border-[#D97706]/40",
  non_determinabile: "bg-stone-100 text-stone-700 border-stone-300",
};

export const StatusBadge = ({ status, label, testid = "result-status" }) => (
  <span data-testid={testid} className={`inline-flex min-h-[36px] items-center gap-2 rounded-full border px-4 text-[16px] font-semibold ${STATUS_STYLE[status] || STATUS_STYLE.non_determinabile}`}>
    <span className="h-2 w-2 rounded-full bg-current" />{label}
  </span>
);

export const pnPending = (r) => r?.comparison?.plantnet_diseases?.agreement === "in_corso" && Date.now() - Date.parse(r.at) < 60000;

function PlantnetNotice({ r }) {
  if (pnPending(r)) return <p className="mt-3 text-[16px] text-[#5b6b60]" data-testid="plantnet-pending">Diagnosi preliminare: confronto con Pl@ntNet Diseases in corso…</p>;
  const text = r.comparison?.plantnet_diseases?.agreement === "in_corso"
    ? "La verifica specialistica con Pl@ntNet non è disponibile in questo momento: la diagnosi si basa sull'analisi delle fotografie."
    : r.plantnet_notice;
  if (!text) return null;
  return <p className="mt-3 rounded-xl border border-[#D97706]/40 bg-[#D97706]/10 p-3 text-[16px] leading-relaxed text-[#7a3d06]" data-testid="plantnet-notice">{text}</p>;
}

function ConsultState({ consult, onRetry }) {
  if (consult?.state === "running") {
    return <p className="flex items-center gap-2 rounded-xl bg-[#f8f3e9] p-3 text-[16px] text-[#5b6b60]" data-testid="consult-pending"><Loader2 size={18} className="shrink-0 animate-spin text-[#B39257]" />Stiamo completando le indicazioni di cura…</p>;
  }
  if (consult?.state === "error") {
    return (
      <div className="rounded-xl border border-[#D97706]/40 bg-[#D97706]/10 p-4" data-testid="consult-error">
        <p className="text-[16px] leading-relaxed text-[#7a3d06]">{consult.error} Le indicazioni qui sopra sono solo quelle preliminari. Il completamento non comporta un nuovo addebito.</p>
        {onRetry && <button onClick={onRetry} className="btn-secondary mt-3 min-h-[44px] bg-white !text-base" data-testid="consult-retry-button"><RotateCcw size={16} /> Completa le indicazioni di cura</button>}
      </div>
    );
  }
  return null;
}

function Verification({ r }) {
  return (
    <ul className="space-y-3">
      {r.verification.map((v, i) => (
        <li key={i} className="text-[17px] leading-relaxed text-[#2D3A30]" data-testid={`result-verification-${i}`}>
          <strong className="text-[#0D2517]">{v.what}</strong>{v.distinguishes && <span className="block text-[16px] text-[#5b6b60]">{v.distinguishes}</span>}
        </li>
      ))}
      {r.photo_request && (
        <li className="flex gap-2 text-[17px] leading-relaxed text-[#2D3A30]" data-testid="result-photo-request"><Camera size={20} className="mt-1 shrink-0 text-[#B39257]" />{r.photo_request.instructions}</li>
      )}
    </ul>
  );
}

function Symptoms({ r }) {
  const obs = (r.details?.observed || []).slice(0, 4);
  if (!obs.length) return null;
  return (
    <div className="mt-4" data-testid="result-symptoms">
      <p className="mb-1 text-[17px] font-semibold text-[#0D2517]">Sintomi principali</p>
      <ul className="list-disc space-y-1 pl-5 text-[17px] leading-relaxed text-[#2D3A30]">{obs.map((o, i) => <li key={i}>{o.text}</li>)}</ul>
    </div>
  );
}

export default function ResultSummary({ result: r, species, consult, onRetryConsult, pro = false, reassessSlot = null }) {
  const needCheck = r.verification?.length > 0 || r.photo_request || reassessSlot;
  const done = consult?.state === "done";
  const v2 = done && consult.version === 2;
  const verification = needCheck && <Verification r={r} />;
  return (
    <div className="space-y-4" data-testid="result-summary">
      <Section icon={Stethoscope} title={v2 ? "Che cos'ha la mia pianta?" : "Cos'ha la tua pianta?"} testid="result-what">
        {species?.common_name && <p className="mb-1 text-[16px] text-[#5b6b60]">{species.common_name} <span className="botanical">{species.botanical_name}</span></p>}
        {r.main_cause && <p className="text-[16px] font-semibold text-[#5b6b60]" data-testid="result-title-label">{r.status === "probabile" ? "Diagnosi più probabile" : "Ipotesi principale, da verificare"}</p>}
        <p className="font-serif text-[25px] font-semibold leading-tight text-[#0D2517]" data-testid="result-title">{r.title}</p>
        {r.main_cause?.agent && <p className="botanical mt-1 text-[17px] text-[#B39257]">{r.main_cause.agent}</p>}
        <div className="mt-3"><StatusBadge status={r.status} label={r.status_label} /></div>
        {r.summary && <p className="mt-3 text-[17px] leading-relaxed text-[#2D3A30]" data-testid="result-summary-text">{r.summary}</p>}
        {r.update_notice && <p className="mt-3 rounded-xl border border-[#D97706]/40 bg-[#D97706]/10 p-3 text-[16px] leading-relaxed text-[#7a3d06]" data-testid="result-update-notice">{r.update_notice}</p>}
        {v2 && <Symptoms r={r} />}
        {v2 && verification && <div className="mt-4 border-t border-[#ddc69c]/60 pt-4"><p className="mb-2 flex items-center gap-2 text-[17px] font-semibold text-[#0D2517]"><SearchCheck size={18} className="text-[#B39257]" />Per una conferma</p>{verification}</div>}
        {pro && <PlantnetNotice r={r} />}
      </Section>

      {(!v2 || pro) && <ResultDetails result={r} pro={pro} consultDone={Boolean(consult)} />}

      {!v2 && verification && <Section icon={SearchCheck} title={reassessSlot ? "Per una conferma" : "Serve una verifica?"} testid="result-verification">{verification}{reassessSlot}</Section>}

      {v2 ? <ResultConsultV2 consult={consult} result={r} /> : done ? <ResultConsult consult={consult} /> : (
        <Section icon={ListChecks} title="Cosa fare adesso?" testid="result-actions">
          <ol className="space-y-3">
            {r.actions.map((a, i) => (
              <li key={i} className="flex gap-3 text-[17px] leading-relaxed text-[#2D3A30]" data-testid={`result-action-${i}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0D2517] text-[15px] font-bold text-[#F7F4EE]">{i + 1}</span>
                <span className="pt-0.5">{a.text}</span>
              </li>
            ))}
          </ol>
          {!consult && r.details?.phyto?.note && <p className="mt-4 rounded-xl bg-[#f8f3e9] p-3 text-[16px] leading-relaxed text-[#2D3A30]" data-testid="result-phyto-note">{r.details.phyto.note}</p>}
          {consult && <div className="mt-4"><ConsultState consult={consult} onRetry={onRetryConsult} /></div>}
        </Section>
      )}
      <p className="text-center text-[14px] text-[#8a958d]">Analisi del {fmtDate(r.at)} · AI preliminare, non sostituisce la diagnosi professionale.</p>
    </div>
  );
}
